import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";

import { dealsRepo } from "data/repositories/dealsRepo";
import { provincesRepo } from "data/repositories/provincesRepo";
import { adminThemes } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { normalizeSlug } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";

function emptyDeal() {
  return {
    id: "",
    provinceId: "trabzon",
    title: "",
    description: "",
    priceText: "",
    startDate: "2025-12-14",
    endDate: "2026-01-31",
    theme: "Nature",
    contactCta: "WhatsApp ile iletişim",
  };
}

function validateDealForm(form, isNew, dealId) {
  const nextErrors = {};
  const normalizedId = form.id ? normalizeSlug(form.id) : normalizeSlug(`${form.provinceId}-${form.title}`);

  if (!normalizedId) nextErrors.id = "Fırsat ID üretilemedi.";
  else if (isNew && dealsRepo.getById(normalizedId)) nextErrors.id = "Bu ID ile kayıt zaten var.";
  else if (!isNew && normalizedId !== dealId) nextErrors.id = "Mevcut kayıtta ID değiştirilemez.";

  if (!form.provinceId) nextErrors.provinceId = "Şehir seçimi zorunludur.";
  if (!form.title.trim()) nextErrors.title = "Başlık zorunludur.";
  if (!form.description.trim()) nextErrors.description = "Açıklama zorunludur.";
  if (!form.priceText.trim()) nextErrors.priceText = "Fiyat metni zorunludur.";
  if (!form.startDate.trim()) nextErrors.startDate = "Başlangıç tarihi zorunludur.";
  if (!form.endDate.trim()) nextErrors.endDate = "Bitiş tarihi zorunludur.";

  return { nextErrors, normalizedId };
}

export default function DealEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const dealId = params.id;
  const isNew = dealId === "new" || !dealId;

  const [form, setForm] = useState(emptyDeal());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const provinces = useMemo(() => provincesRepo.list().filter((province) => !province.deletedAt), []);

  useEffect(() => {
    setError("");
    if (isNew) {
      setForm(emptyDeal());
      setErrors({});
      return;
    }
    const current = dealsRepo.getById(dealId);
    if (!current) {
      setError("Fırsat bulunamadı");
      return;
    }
    setForm(current);
    setErrors({});
  }, [isNew, dealId]);

  const onSave = (e) => {
    e.preventDefault();
    setError("");
    const { nextErrors, normalizedId } = validateDealForm(form, isNew, dealId);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    const payload = {
      ...form,
      id: normalizedId,
      title: form.title.trim(),
      description: form.description.trim(),
      priceText: form.priceText.trim(),
      startDate: form.startDate.trim(),
      endDate: form.endDate.trim(),
      contactCta: form.contactCta.trim(),
    };

    try {
      setSaving(true);
      if (isNew) {
        dealsRepo.create(payload);
      } else {
        dealsRepo.update(form.id, payload);
      }

      navigate(`/admin${adminPaths.content.deals.list}`, {
        state: {
          status: {
            color: "success",
            message: `${payload.title} başarıyla ${isNew ? "oluşturuldu" : "güncellendi"}.`,
          },
        },
      });
    } catch (err) {
      setError(err?.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="primary"
        title={isNew ? "Yeni fırsat" : "Fırsat düzenle"}
        description="Şehir ve tema bilgisi mobil kampanya akışlarıyla uyumlu tutulur."
        backAction={() => navigate(`/admin${adminPaths.content.deals.list}`)}
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Detay</h3>
              </CardHeader>
              <CardBody>
                {error ? <Alert color="danger">{error}</Alert> : null}

                <Form onSubmit={onSave}>
                  <FormGroup>
                    <Label>ID</Label>
                    <Input
                      value={form.id}
                      onChange={(e) => setForm((p) => ({ ...p, id: normalizeSlug(e.target.value) }))}
                      disabled={!isNew}
                      placeholder="Boş bırakırsanız başlık ve ilden türetilir"
                      invalid={Boolean(errors.id)}
                    />
                    <FormFeedback>{errors.id}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Şehir</Label>
                    <Input
                      type="select"
                      value={form.provinceId}
                      onChange={(e) => setForm((p) => ({ ...p, provinceId: e.target.value }))}
                      invalid={Boolean(errors.provinceId)}
                    >
                      {provinces.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.id})
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{errors.provinceId}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Tema</Label>
                    <Input type="select" value={form.theme} onChange={(e) => setForm((p) => ({ ...p, theme: e.target.value }))}>
                      {adminThemes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>Başlık</Label>
                    <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} invalid={Boolean(errors.title)} />
                    <FormFeedback>{errors.title}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Açıklama</Label>
                    <Input
                      type="textarea"
                      rows="5"
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                      invalid={Boolean(errors.description)}
                    />
                    <FormFeedback>{errors.description}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Başlangıç</Label>
                        <Input
                          value={form.startDate}
                          onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                          placeholder="GG-AA-YYYY"
                          invalid={Boolean(errors.startDate)}
                        />
                        <FormFeedback>{errors.startDate}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Bitiş</Label>
                        <Input
                          value={form.endDate}
                          onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                          placeholder="GG-AA-YYYY"
                          invalid={Boolean(errors.endDate)}
                        />
                        <FormFeedback>{errors.endDate}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Fiyat metni</Label>
                    <Input
                      value={form.priceText}
                      onChange={(e) => setForm((p) => ({ ...p, priceText: e.target.value }))}
                      placeholder="₺990 / kişi"
                      invalid={Boolean(errors.priceText)}
                    />
                    <FormFeedback>{errors.priceText}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>CTA</Label>
                    <Input value={form.contactCta} onChange={(e) => setForm((p) => ({ ...p, contactCta: e.target.value }))} placeholder="WhatsApp ile iletişim" />
                  </FormGroup>

                  <AdminFormActions saving={saving} onCancel={() => navigate(`/admin${adminPaths.content.deals.list}`)} />
                </Form>

                <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                  TODO(firebase): deals koleksiyonu + partner bağlantıları.
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Notlar</h3>
              </CardHeader>
              <CardBody>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Bu sayfa mobildeki Deals List / Deal Detail ekranlarını besleyecek içerik içindir.
                </div>
                {!isNew ? (
                  <div className="mt-3">
                    <Label className="d-block">Kayıt durumu</Label>
                    <Badge color={dealsRepo.getById(dealId)?.deletedAt ? "warning" : "success"} pill>
                      {dealsRepo.getById(dealId)?.deletedAt ? "Arşivde" : "Aktif"}
                    </Badge>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
